import { useQuery } from 'react-query';
import { collectionsDuck } from '@eb/redux-destination';
import { sdkRequest } from '@eb/http';

import { UserCollections, User, Collection } from '../types';

export const getUser = async (): Promise<User> => {
    const user = await sdkRequest('/api/v3/users/me/');
    return user;
};

const getUserCollections = async (
    _: string,
    id: string,
): Promise<UserCollections> => {
    const collections = await collectionsDuck.api.getUserCollections(id);
    return { collections: collections.collections };
};

interface UseUserArguments {
    user?: User;
}

interface UseUserResponse {
    fetchUserData: Function;
    collections?: Collection[];
    id?: string;
    firstName?: string;
    lastName?: string;
    name?: string;
}

export const useUser = (config: UseUserArguments): UseUserResponse => {
    const { user } = config;

    //Get user object to grab logged in user ID if not passed through
    const { data: userObject, refetch: fetchUser } = useQuery('user', getUser, {
        initialData: () => {
            if (user) {
                return user;
            }

            return undefined;
        },
        enabled: false,
        retry: false,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        refetchOnMount: false,
        staleTime: Infinity,
    });

    const userId = userObject?.id;

    const { data, refetch: fetchUserCollections } = useQuery(
        ['userCollections', userId],
        getUserCollections,
        {
            enabled: false,
            refetchOnWindowFocus: false,
            refetchOnReconnect: false,
            refetchOnMount: false,
            staleTime: Infinity,
        },
    );

    const fetchUserData = () => {
        if (!userId) {
            return fetchUser().then(() => fetchUserCollections());
        } else if (!data?.collections) {
            return fetchUserCollections();
        }

        return Promise.resolve();
    };

    return {
        fetchUserData,
        id: userId,
        firstName: userObject?.first_name,
        lastName: userObject?.last_name,
        name: userObject?.name,
        collections: data?.collections,
    };
};
